<template>
  <div id="plan-table">
    <div class="md-layout">
      <div class="md-layout-item">
        <div class="btn-row">
          <md-button class="md-success" @click="onPlanGenerate">
            {{ $t('button.add') }}
          </md-button>
        </div>
        <md-card>
          <transition name="fade">
            <div v-if="isPlanListLoading" class="report-load-progress">
              <md-progress-bar
                md-mode="indeterminate"
                class="progress-bar"
              ></md-progress-bar>
            </div>
          </transition>
          <md-card-header
            class="md-card-header-icon md-card-header-green card-icon-header"
          >
            <div class="card-icon">
              <md-icon>view_list</md-icon>
            </div>
            <h4 class="title">{{ $t('label.plan_list') }}</h4>
          </md-card-header>
          <md-card-content>
            <md-table
              :value="queriedData"
              :md-sort.sync="currentSort"
              :md-sort-order.sync="currentSortOrder"
              :md-sort-fn="customSort"
              class="paginated-table table-striped table-hover"
            >
              <md-table-toolbar>
                <md-field>
                  <label for="pages">{{ $t('label.per_page') }}</label>
                  <md-select v-model="pagination.perPage" name="pages">
                    <md-option
                      v-for="item in pagination.perPageOptions"
                      :key="item"
                      :label="item"
                      :value="item"
                    >
                      {{ item }}
                    </md-option>
                  </md-select>
                </md-field>

                <md-field>
                  <md-input
                    type="search"
                    class="mb-3"
                    clearable
                    style="width: 200px"
                    :placeholder="$t('label.search_records')"
                    v-model="searchQuery"
                  ></md-input>
                </md-field>
              </md-table-toolbar>

              <md-table-row slot="md-table-row" slot-scope="{item}">
                <md-table-cell :md-label="$t('tables.map')" class="cell-map">
                  <div>
                    <md-button
                      class=" md-success md-just-icon md-round md-simple"
                      @click.stop.prevent="onShowPlanOnTheMap(item.id)"
                    >
                      <i class="fas fa-map-marked"></i>
                    </md-button>
                  </div>
                </md-table-cell>
                <md-table-cell
                  v-for="header of headers"
                  :key="header"
                  :md-label="$t(`plans.table_${header}`)"
                  :md-sort-by="header"
                  :class="[
                    ['state', 'created_at'].includes(header)
                      ? 'md-center'
                      : 'md-numeric'
                  ]"
                >
                  {{ item[header] }}
                </md-table-cell>
                <md-table-cell
                  class="btn-cont"
                  :md-label="$t('tables.actions')"
                >
                  <div class="cell-actions">
                    <md-button
                      class="md-success md-raised md-sm"
                      @click.stop.prevent="onEditParams(item.id)"
                    >
                      {{ ` ${$t('button.edit')}` }}
                    </md-button>
                    <md-button
                      class="md-default md-raised md-sm"
                      @click.stop.prevent="onPlanDetails(item)"
                    >
                      {{ $t('button.details') }}
                    </md-button>
                  </div>
                </md-table-cell>
              </md-table-row>
            </md-table>
          </md-card-content>
          <md-card-actions md-alignment="space-between">
            <div class>
              <p class="card-category">
                {{
                  $t('label.showing_from_to_of_entries', {
                    from: to === 0 ? 0 : from + 1,
                    to: to,
                    total
                  })
                }}
              </p>
            </div>
            <pagination
              class="pagination-no-border pagination-success"
              v-model="pagination.currentPage"
              :per-page="pagination.perPage"
              :total="total"
            ></pagination>
          </md-card-actions>
        </md-card>
      </div>
    </div>
  </div>
</template>

<script>
import {mapState} from 'vuex'
import {Pagination} from '@/components'
//import {SlideYDownTransition} from 'vue2-transitions'
import Fuse from 'fuse.js'

export default {
  name: 'plan-list',
  data() {
    return {
      pagination: {
        perPage: 10,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50],
        total: 0
      },
      currentSort: 'none',
      currentSortOrder: 'asc',
      searchQuery: '',
      propsToSearch: ['key'],
      searchedData: [],
      fuseSearch: null,
      tableData: [],
      isPlanListLoading: false,
      headers: [
        'id',
        'total_budget',
        'periodic_budget',
        'routine_budget',
        'state',
        'created_at',
        'periodic_sum',
        'routine_sum'
      ]
    }
  },
  async created() {
    this.isPlanListLoading = true
    try {
      this.tableData = await this.$store.dispatch('LOAD_PLAN_LIST')
      // Fuse search initialization.
      this.fuseSearch = new Fuse(this.tableData, {
        keys: [...this.headers],
        threshold: 0.3
      })
      this.tableData.forEach((plan, index) => {
        this.$store.dispatch('LOAD_PLAN_PARAMS', plan.id).then((item) => {
          this.$set(
            this.tableData[index],
            'routine_sum',
            item.routine_sum ? item.routine_sum : 0
          )
          this.$set(
            this.tableData[index],
            'periodic_sum',
            item.periodic_sum ? item.periodic_sum : 0
          )
        })
      })
    } catch (err) {
    } finally {
      this.isPlanListLoading = false
    }
  },
  components: {
    Pagination
    //SlideYDownTransition
  },
  methods: {
    onShowPlanOnTheMap(id) {
      this.$router.push({
        name: 'Map',
        query: {vector: `plan_${id.toString()}`, pagestyle: 'plan'}
      })
    },
    onEditParams(id) {
      this.$router.push({
        name: 'Plan-params',
        params: {plan_id: id.toString()}
      })
    },
    onPlanDetails(plan) {
      const {id, state} = plan
      this.$router.push({
        name: 'Planning-data',
        params: {plan_id: id.toString()},
        query: {type: state}
      })
    },
    async onPlanGenerate() {
      try {
        const id = await this.$store.dispatch('PLAN_GENERATE')
        this.onEditParams(id)
      } catch (err) {}
    },
    customSort(value) {
      return value.sort((a, b) => {
        const sortBy = this.currentSort
        const aValue = a[sortBy] === null ? '' : a[sortBy].toString()
        const bValue = b[sortBy] === null ? '' : b[sortBy].toString()

        if (this.currentSortOrder === 'desc') {
          return aValue.localeCompare(bValue, undefined, {numeric: true})
        }
        return bValue.localeCompare(aValue, undefined, {numeric: true})
      })
    }
  },
  computed: {
    queriedData() {
      const result = !this.searchQuery
        ? this.tableData
        : this.searchedData
        ? this.searchedData
        : []
      return result.slice(this.from, this.to)
    },
    to() {
      let highBound = this.from + this.pagination.perPage
      if (this.total < highBound) {
        highBound = this.total
      }
      return highBound
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1)
    },
    total() {
      return this.searchQuery ? this.searchedData.length : this.tableData.length
    }
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData
      if (value !== '') {
        result = this.fuseSearch.search(this.searchQuery)
      }
      this.searchedData = result
    }
  }
}
</script>

<style lang="scss">
.md-center {
  text-align: center;
}

.md-table-cell-container {
  margin-right: 32px;
}

.md-table .md-table-head {
  text-align: center;
}

#plan-table .paginated-table table > tbody > tr > td {
  width: unset;
}
.cell-map {
  .md-button {
    margin: 3px 0;
    min-width: 100px;
  }
}

.cell-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  .md-button {
    margin: 3px 0;
    min-width: 100px;
  }
}

.report-load-progress {
  position: absolute;
  width: 100%;
  /* height: 100%; */
  top: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;
  .progress-bar {
    width: 100%;
  }
}

.btn-row {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.card-icon-header {
  z-index: 10;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.7s;
}
.fade-enter, .fade-leave-to /* .fade-leave-active до версии 2.1.8 */ {
  opacity: 0;
}
</style>
